import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const ParasharLake = () => {
  const tourData = {
    name: "Parashar Lake Trek",
    duration: "01 Night 02 Days",
    price: "₹ 2,499/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935182/a1xo4ziayszs7vli9mgy.jpg",
    altitude: "8,956 ft",
    difficulty: "Easy to Moderate",
    region: "Himachal Pradesh",
    highlights: ["Sacred lake", "Temple", "Weekend getaway"],
    bestTime: "March to November",
    description: "The Parashar Lake Trek is a perfect weekend getaway that takes you to a beautiful sacred lake surrounded by lush green meadows and snow-capped peaks. This easy to moderate trek is ideal for beginners and offers stunning views of the Himalayas."
  };

  return (
    <>
      <Helmet>
        <title>Parashar Lake Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Parashar Lake Trek in Himachal Pradesh. Perfect weekend trek to a sacred lake with stunning mountain views and temple visit." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              A perfect weekend trek to a beautiful sacred lake
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek is perfect for those looking for a short spiritual adventure in the Dhauladhar range. 
                    The crystal-clear waters of Parashar Lake reflect the surrounding peaks, creating a 
                    picture-perfect setting for photography and relaxation. The ancient temple adds to the spiritual significance of the place.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Sacred lake with spiritual significance</li>
                    <li>Ancient temple visit</li>
                    <li>Perfect weekend getaway</li>
                    <li>Beginner-friendly trek</li>
                    <li>Stunning mountain views</li>
                    <li>Lush green meadows</li>
                    <li>Photography opportunities</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Arrival at Mandi (26 km by road and 9 km Trek from Baggi)</h4>
                    <ul>
                      <li><strong>Morning:</strong> After reaching Mandi, a 26 km drive will take participants to the scenic Baggi village, starting at 10 AM.</li>
                      <li><strong>Lunch & Trek Start:</strong> Upon reaching Baggi, a packed lunch will be provided for energy before the Prashar Lake trek commences after a brief break.</li>
                      <li><strong>Trekking:</strong> The trek involves high and low sections through straightforward and picturesque trails, leading to the lakeside. The trekking duration is approximately 4-5 hours.</li>
                      <li><strong>Evening:</strong> Upon reaching the lake in the evening, a campfire will be illuminated for enjoyment. Snacks and dinner will be provided.</li>
                      <li><strong>Accommodation:</strong> Participants will spend the evening in camps next to Prashar Lake.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Sightseeing in Prashar Lake | Departure from Mandi</h4>
                    <ul>
                      <li><strong>Morning:</strong> After a healthy breakfast, a guide will showcase the brilliant landscapes and striking views of the Himalayas.</li>
                      <li><strong>Temple Visit & Return Trek:</strong> Participants will visit a nearby temple and then begin the journey back towards Baggi. A caution is given for slippery trails and potential weight on shoulders during the descent.</li>
                      <li><strong>Departure:</strong> A drive to Mandi will commence upon arrival in Baggi, expected around 3 PM, followed by departure to respective destinations.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Camping and stay sharing basis</li>
                    <li>3 Meals and snacks within the evening</li>
                    <li>Trekking Gears: Tents, Sleeping Bags and Mattress</li>
                    <li>Professional Guide</li>
                    <li>We've rich experience with this region & definitely, many stories</li>
                    <li>Forest Permits</li>
                    <li>Basic Medical Kit</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>All the transportation to and from the bottom Camp in Mandi</li>
                    <li>Shower or stay within the Guest House</li>
                    <li>Any meals not mentioned above</li>
                    <li>Personal expenses and insurance</li>
                    <li>Personal trekking gear</li>
                    <li>Porters or mules to carry personal luggage</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>8,956 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>18 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy to Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>March to November</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Mandi)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 2,499 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 2,999 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 3,499 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Mandi:</strong> Mandi Bus Stand between 08:00 AM to 09:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Sacred lake with spiritual significance</li>
                    <li>Ancient temple visit</li>
                    <li>Perfect weekend getaway</li>
                    <li>Beginner-friendly trek</li>
                    <li>Stunning mountain views</li>
                    <li>Lush green meadows</li>
                    <li>Photography opportunities</li>
                    <li>Professional guide with local stories</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default ParasharLake;
